import React from "react";

import DatePicker from "react-datepicker";
import moment from "moment";
import Modal from "react-responsive-modal";
import { Tooltip } from "react-tippy";
import * as Icon from "react-feather";

import "react-tippy/dist/tippy.css";

import { DEFAULT_RELATIVE_DUE_DATE } from "../../../constants/EngagementTemplates";
import WORKFLOW from "../../../constants/Workflow";
import EngagementTaskModal from "../EngagementTemplates/eng_task_modal/EngagementTaskModal";
import UtilityService from "../../../services/UtilityService";

import SearchableDropdown from "../../common/searchable_dropdown/SearchableDropdown";
import UserService from "../../../services/UserService";
import EngagementService from "../../../services/EngagementService";
import ClientService from "../../../services/ClientService";
import ContactService from "../../../services/ContactService";
import WComponent from "../../common/WComponent";
import TextFieldInput from "../../common/text_field_input/TextFieldInput";
import UserAssigner from "../../common/user_assigner/UserAssigner";
import Tabs from "../../common/tabs/Tabs";
import Tab from "../../common/tabs/tab/Tab";
import RRuleEditor from "../../common/RRule/RRuleEditor";
import EngagementTaskCard from "../../common/eng_task_card/EngagementTaskCard";
import RelativeDate from "../../common/RelativeDate/RelativeDate";
import DateSelector from "../../common/date_selector/DateSelector";
import Button from "../../common/button/Button";
import Checkbox from "../../common/checkbox/Checkbox";
import AddItemButton from "../../common/add_item_button/AddItemButton";

import "./engagement-modal.css";
import RequiredLabel from "../../common/required_label";
import EngagementBudgetSettings from "../eng_budget/Eng_Budget";
import EngagementBudgetCard from "../../common/eng_budget_card/EngagementBudgetCard";
import { NAME_CONVENTIONS } from "../engagement/engagementNameConventions";
import CLIENTS from "../../../constants/Clients";

const TABS = {
	tasks: "tasks",
	users: "users",
	repeats: "repeats",
	budgets: "budgets"
};

const PRIORITY_OPTIONS = [{ id: null, name: "None" }, ...Object.values(WORKFLOW.priorities).map(priority => ({ id: priority.key, name: priority.value }))];

const { alert } = UtilityService;

class EngagementModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			show: false,
			namingConvention: NAME_CONVENTIONS[0].id,

			teamId: null,
			flowId: null,
			flowStateId: null,
			clientId: 0,
			name: "",
			dateIn: moment(new Date()).format("YYYY-MM-DD hh:mm:ss a"),
			dueDate: moment(new Date()).format("YYYY-MM-DD hh:mm:ss a"),
			fiscalYearEnd: new Date(),
			notes: "",

			// Eng Task Data
			engagementTaskAction: "new",
			engTaskUpdateIndex: null,
			showEngagementTaskModal: false,

			flows: [],
			flowStates: [],

			clientName: "",
			managedByName: "",
			subContacts: [],
			selectedSubContactName: "",
			selectedTeamName: "",
			selectedTab: "tasks",
			selectedFlowStateName: "",
			tasks: [],
			assignedUsers: [],
			roles: [],
			budgets: [],
			rrule: "",
			relativeDueDate: DEFAULT_RELATIVE_DUE_DATE,
			recurrenceStartDate: new Date(),
			taxEstimate: false,
			sequentialTasks: true,
			priority: null,
			selectedTemplate: "",
		};

		this.clientSearchableDropdown = null;
	}

	componentDidMount() {
		this.resetComponent();
	}

	componentDidUpdate(prevProps) {
		let { show, flowId } = this.props;

		if (prevProps.show !== show || prevProps.flowId !== flowId) {
			this.resetComponent();
		}
	}

	resetComponent = async () => {
		let { show, clientId, flowId } = this.props;

		await this.update({ loading: true });

		await this.update({
			show,
			flowId,
			clientId: clientId || 0,
			clientName: "",
			managedByName: "",
			subContacts: [],
			selectedSubContactName: "",
			namingConvention: NAME_CONVENTIONS[0].id,
		});

		await this.setFlow();

		if (clientId) {
			let client = await ClientService.fetchClient({ clientId });

			await this.update({
				clientName: client.client.name,
				managedByName: ClientService.getManagedByName(client.client) || ""
			});

			await this.fetchSubContacts(clientId, client.client.type);
		}

		await this.fetchAllRoles();
		await this.update({ loading: false });
	};

	fetchAllRoles = async () => {
		let roles = await UserService.fetchRoles({});

		await this.update({
			roles: roles
		});
	};

	onDateInSelect = async (date) => {
		await this.update({ 
			dateIn: date
		});
		this.setName({ dateIn: date });
	};

	onDueDateSelect = async (date) => {
		await this.update({
			dueDate: date
		});
		this.setName({ dueDate: date });
	};

	onFiscalYearEndSelect = async date => {
		await this.update({
			fiscalYearEnd: date
		});

		await this.setName({ fiscalYearEnd: date });
		await this.setDueDate();
	};

	setFlow = async ({ flow } = {}) => {
		let { flowId } = this.state;

		if (!flow) {
			flow = await EngagementService.fetchFlow({ flowId });
		}

		await this.update({
			flowId: flow.id,
			flowCode: flow.code,
			flowName: flow.name,

			flowStateId: null,
			selectedFlowStateName: ""
		});
	};

	onFlowSelect = async flow => {
		let { flowId } = this.state;

		// User selected same flow, dipset
		if (flowId === flow.id) {
			return;
		}

		await this.setFlow({ flow });

		await this.setName();
		await this.setDueDate();
	};

	onFlowStateSelect = async flowState => {
		await this.update({
			flowStateId: flowState.id,
			selectedFlowStateName: flowState.name
		});
	};

	setDueDate = async () => {
		let { flowCode, fiscalYearEnd, namingConvention } = this.state;
		let dueDate = EngagementService.deriveDueDateForEngagement({ flowCode, fiscalYearEnd });

		const next = { dueDate };
		if (namingConvention === "DUE_DATE" || namingConvention === "TEMPLATE_1") {
			next.name = this.buildEngagementName({ dueDate });
		}

		await this.update(next);
	};

	formatNameDate = (value) => {
		const m = moment(value);
		return m.isValid() ? m.format("YYYY-MM-DD") : "";
	};

	buildEngagementName = (overrides = {}) => {
		const {
			namingConvention = this.state.namingConvention,
			flowName = this.state.flowName,
			clientName = this.state.clientName,
			selectedSubContactName = this.state.selectedSubContactName,

			fiscalYearEnd = this.state.fiscalYearEnd,
			dueDate = this.state.dueDate,
			selectedTemplate = this.state.selectedTemplate,
		} = overrides;

		const isTemplateConvention = namingConvention && namingConvention.startsWith("TEMPLATE");

		// For TEMPLATE conventions, prefix should be Template name instead of Workflow type
		const prefix = isTemplateConvention ? selectedTemplate || "" : flowName || "";

		// Map TEMPLATE_* to the same suffix behavior as existing conventions
		let suffixMode = namingConvention;
		if (namingConvention === "TEMPLATE") suffixMode = "FYE";
		else if (namingConvention === "TEMPLATE_1") suffixMode = "DUE_DATE";
		else if (namingConvention === "TEMPLATE_3") suffixMode = "YEAR";
		else if (namingConvention === "TEMPLATE_4") suffixMode = "MONTH_YEAR";

		let suffix = "";

		if (suffixMode === "DUE_DATE") suffix = this.formatNameDate(dueDate);
		else if (suffixMode === "YEAR") suffix = moment().format("YYYY");
		else if (suffixMode === "MONTH_YEAR") suffix = moment().format("MMMM YYYY");
		else suffix = this.formatNameDate(fiscalYearEnd);

		// Optional sub contact tag: "Flow - (Relation : Contact) Client - suffix"
		const subContactChunk = selectedSubContactName ? `(${selectedSubContactName}) ` : "";

		return `${prefix} - ${subContactChunk}${clientName || ""} - ${suffix}`;
	};

	setName = async (nameData = {}) => {
		const newName = this.buildEngagementName(nameData);
		await this.update({ name: newName });
	};

	onTabSelect = async selectedTab => {
		await this.update({ selectedTab: selectedTab.id });
	};

	clearModal = async () => {
		await this.update({
			name: "",
			namingConvention: NAME_CONVENTIONS[0].id,

			teamId: 0,
			clientId: 0,
			// flowId: null,
			flowStateId: null,

			fiscalYearEnd: new Date(),
			dateIn: new Date(),
			dueDate: new Date(),
			recurrenceStartDate: new Date(),

			assignedUsers: [],

			selectedTeamName: "",
			selectedFlowStateName: "",
			tasks: [],
			budgets: [],
			taxEstimate: false,
			sequentialTasks: true,
			priority: null,
			selectedTemplate: "",

			subContacts: [],
			selectedSubContactName: "",
		});

		this.clientSearchableDropdown.clear();
	};

	createEngagement = async () => {
		let {
			name,
			teamId,
			clientId,
			flowId,
			flowStateId,
			dateIn,
			dueDate,
			fiscalYearEnd,
			assignedUsers,
			tasks,
			rrule,
			relativeDueDate,
			loading,
			taxEstimate,
			sequentialTasks,
			priority,
			budgets,
			namingConvention
		} = this.state;

		let created = false;

		const missing = this.getMissingRequiredFields();
		if (missing.length) {
			alert({ type: "error", text: `Please fill: ${missing.join(", ")}` });
			return created;
		}

		if (loading) {
			alert({ type: "info", text: "Updating ..." });
			return;
		}

		await this.update({ loading: true });

		try {
			let { data } = await EngagementService.create({
				name,
				namingConvention,
				teamId,
				clientId,
				flowId,
				flowStateId,

				dueDate: moment(dueDate).format("YYYY-MM-DD"),
				dateIn: moment(dateIn).format("YYYY-MM-DD"),
				fiscalYearEnd: moment(fiscalYearEnd).format("YYYY-MM-DD"),

				assignedUsers,
				tasks,
				budgets: budgets,

				rrule,
				relativeDueDate,
				taxEstimate,
				sequentialTasks,
				priority
			});

			this.clearModal();

			created = data.id;
		} catch (error) {
			console.log(error);

			created = null;
		}

		await this.update({ loading: false });

		return created;
	};

	onAddBudget = async () => {
		if (!this.budgetSettingsRef) return;
		const { resource = "", budget = "" } = this.budgetSettingsRef.state || {};
		const trimmedResource = (resource || "").trim();
		const trimmedBudget = (budget || "").trim();
		if (!trimmedResource || !trimmedBudget) {
			UtilityService.alert({ type: "error", text: "Please enter Resource and Budget time." });
			return;
		}
		const next = this.state.budgets.slice();
		next.push({ resource: trimmedResource, budget: trimmedBudget });
		await this.update({ budgets: next });
		this.budgetSettingsRef.update({ resource: "", budget: "" });
	};

	onRemoveBudget = (index) => {
		const next = this.state.budgets.slice();
		next.splice(index, 1);
		this.update({ budgets: next });
	};


	onCreate = async () => {
		let created = await this.createEngagement();

		if (created && this.props.onCreate) {
			this.update({
				show: false
			});

			this.props.onCreate(created);
		}
	};

	onClose = async () => {
		if (this.props.onClose) {
			this.props.onClose();
		}

		this.clearModal();
	};

	onClientSelect = async item => {
		if (this.state.clientId === item.id) {
			return;
		}

		await this.update({
			clientId: item.id,
			clientName: item.name,

			subContacts: [],
			selectedSubContactName: ""
		});

		await this.setName();
		await this.fetchManagedByName(item.id);
		await this.fetchSubContacts(item.id, item.type);
	};

	fetchSubContacts = async (clientId, clientType) => {
		let subContacts = [];

		if (clientId && clientType === CLIENTS.clientTypes.personal.id) {
			let response = await ContactService.getListOfContacts({ clientId });
			let contacts = response?.data || [];

			// Parens are stripped so the tag always matches the backend's recurring-name preservation pattern
			let clean = value => (value || "").replace(/[()]/g, "").trim();

			subContacts = contacts
				.map(contact => {
					let relation = clean(contact.Clients?.[0]?.ContactClient?.relation);
					let contactName = clean(`${contact.first_name || ""} ${contact.last_name || ""}`);

					return contactName ? { id: contact.id, name: relation ? `${relation} : ${contactName}` : contactName } : null;
				})
				.filter(Boolean);
		}

		// A slower response for a previously selected client must not land on the current one
		if (clientId !== this.state.clientId) {
			return;
		}

		await this.update({ subContacts });
	};

	onSubContactSelect = async item => {
		await this.update({
			selectedSubContactName: item.id ? item.name : ""
		});

		await this.setName();
	};

	fetchManagedByName = async clientId => {
		let managedByName = "";

		if (clientId) {
			const response = await ClientService.getClient({ clientId });
			managedByName = ClientService.getManagedByName(response?.data?.client) || "";
		}

		await this.update({ managedByName });
	};

	onNamingConventionSelect = async (item) => {
		if (!item?.id) return;

		await this.update({ namingConvention: item.id });
		await this.setName({ namingConvention: item.id });
	};

	onPrioritySelect = async item => {
		await this.update({ priority: item.id });
	};

	isValid = () => this.getMissingRequiredFields().length === 0;

	onChange = e => {
		let { name, value, type, checked } = e.target;

		if (type === "checkbox") {
			value = checked;
		}

		this.update({
			[name]: value
		});
	};

	onAssignedUsersChanged = async ({ selectedUsers }) => {
		let newAssignedUsers = selectedUsers.slice();

		await this.update({
			assignedUsers: newAssignedUsers
		});
	};

	onSelectTeam = async team => {
		let { onFetchUsersByTeam } = this;
		// update the team
		await this.update({
			teamId: team.id,
			selectedTeamName: team.name
		});

		// fetch users associated with the team
		await onFetchUsersByTeam({ teamId: team.id });
	};

	onFetchUsersByTeam = async ({ teamId }) => {
		let team = await UserService.fetchTeam({ teamId });

		const defaultUsers = (team.users || []).filter(u => !!(u.isDefault));

		// update the assigend users of the selected new team
		await this.update({
			assignedUsers: defaultUsers
		});
	};

	getFlowStatusName = async ({ flowId, flowStateId }) => {
		let flowStatusName = "";
		let flowStates = await EngagementService.fetchFlowStates({ flowId });

		// loop through the flow state and get the flow state name that matches the flow state id
		for (let i = 0; i < flowStates.length; i++) {
			if (flowStates[i].id === flowStateId) {
				flowStatusName = flowStates[i].name;
			}
		}

		return flowStatusName;
	};

	findRole = idToQuery => {
		let { roles } = this.state;

		const result = roles.find(item => item.id === idToQuery);
		let name = "";

		if (result) {
			name = result.name;
		} else {
			console.log("Object not found with ID:", idToQuery);
		}

		return name;
	};

	onSelectTemplate = async template => {
		let team = null;
		if (template.team_id) {
			team = await UserService.fetchTeam({ teamId: template.team_id });
		}

		await this.update({
			flowId: template.flow_id,
			selectedTemplate: template.name || "",
		});

		await this.setFlow();

		await this.update({
			teamId: template.team_id || null,
			selectedTeamName: team?.name || "",

			flowStateId: template.flow_state_id,
			selectedFlowStateName: await this.getFlowStatusName({ flowId: template.flow_id, flowStateId: template.flow_state_id }),

			assignedUsers: (team?.users || []).filter(u => !!(u.isDefault)),
			tasks: JSON.parse(template.tasks)
		});

		await this.setName();
		await this.setDueDate();
	};

	onRRuleChange = async ({ rrule }) => {
		await this.update({ rrule });
	};

	onNewEngTask = async () => {
		await this.update({
			engagementTaskAction: "new",
			engTaskUpdateIndex: null,
			showEngagementTaskModal: true
		});
	};

	onUpdateEngTask = async index => {
		await this.update({
			engagementTaskAction: "update",
			engTaskUpdateIndex: index,
			showEngagementTaskModal: true
		});
	};

	onRemoveTask = index => {
		let { tasks } = this.state;
		let prevTasks = tasks;
		prevTasks.splice(index, 1);

		this.update({
			tasks: prevTasks
		});
	};

	onRelativeDueDateChange = async relativeDueDate => {
		await this.update({ relativeDueDate });
	};


	getMissingRequiredFields = () => {
		const { name, clientId, flowId, flowStateId, dateIn, dueDate, fiscalYearEnd } = this.state;

		const missing = [];

		if (!name?.trim()) missing.push("Name");
		if (!clientId) missing.push("Client");
		if (!flowId) missing.push("Workflow Type");	
		if (!flowStateId) missing.push("Status");
		if (!fiscalYearEnd) missing.push("Fiscal Year End");
		if (!dateIn) missing.push("Date In");
		if (!dueDate) missing.push("Due Date");

		return missing;
	};

	render() {
		let {
			// View Toggles
			show,

			// Engagement Data
			name,
			fiscalYearEnd,
			dateIn,
			dueDate,
			taxEstimate,
			sequentialTasks,
			tasks,
			assignedUsers,

			rrule,
			relativeDueDate,
			recurrenceStartDate,
			// Eng Task Data
			showEngagementTaskModal,
			engagementTaskAction,
			engTaskUpdateIndex,

			// Display Items & Dirty Bits
			clientName,
			subContacts,
			selectedSubContactName,
			flowName,
			flowCode,
			selectedTeamName,
			selectedFlowStateName,
			selectedTemplate,
			selectedTab
		} = this.state;

		return (
			<Modal open={show} onClose={this.onClose} center>
				<EngagementTaskModal
					show={showEngagementTaskModal}
					showAssignedBy
					action={engagementTaskAction}
					onClose={() => {
						this.update({
							engagementTaskAction: "new",
							engTaskUpdateIndex: null,
							showEngagementTaskModal: false
						});
					}}
					engTasks={tasks}
					taskToUpdateIndex={engTaskUpdateIndex}
				/>

				<div className="eng-modal">
					<div className="modal-title">New Engagement</div>
					<div className="eng-modal__body">
						<div className="eng-modal__body__fields">
							<div className="eng-modal__body__fields__container">
								<SearchableDropdown
									title="Template"
									onSelect={this.onSelectTemplate}
									type={"engTemplate"}
									selectedName={selectedTemplate}
									showAll
									className={"ft-eng-mod-template"}
								/>

								<SearchableDropdown
									title="Naming Convention"
									options={NAME_CONVENTIONS}
									selectedName={NAME_CONVENTIONS.find((x) => x.id === this.state.namingConvention)?.name || ""}
									onSelect={this.onNamingConventionSelect}
									showAll
									selectEmptyOnClear={false}
									className={"ft-eng-mod-nameconvention"}
								/>

								<TextFieldInput title={<RequiredLabel text="Name (Will Auto Populate)" />} name="name" onChange={this.onChange} autoComplete="off" value={name} />

								<SearchableDropdown
									title={<RequiredLabel text="Client" />}
									ref={ref => (this.clientSearchableDropdown = ref)}
									selectedName={clientName}
									onSelect={this.onClientSelect}
									type={"clients"}
									className={"ft-eng-mod-client"}
								/>
								{this.state.managedByName && <div className="eng-modal__managed-by ft-eng-mod-managedby">Managed by: {this.state.managedByName}</div>}
								{subContacts.length > 0 && (
									<SearchableDropdown
										title={
											<span className="eng-modal__sub-contact-title">
												Sub Contact
												<Tooltip
													tag="span"
													arrow
													position="top"
													title="Optional. Tags this engagement to one of the client's contacts by adding '(Relation : Contact)' before the client's name in the engagement name. Recurring engagements keep the tag."
												>
													<Icon.Info className="eng-modal__sub-contact-title__info-icon" size={15} />
												</Tooltip>
											</span>
										}
										options={subContacts}
										selectedName={selectedSubContactName}
										onSelect={this.onSubContactSelect}
										showAll
										className={"ft-eng-mod-subcontact"}
									/>
								)}
								<SearchableDropdown
									title="Team"
									onSelect={this.onSelectTeam}
									type={"teams"}
									selectedName={selectedTeamName}
									showAll
									className={"ft-eng-mod-team"}
								/>

								<Checkbox
									title="Tax Estimate"
									name="taxEstimate"
									checked={taxEstimate}
									onChange={this.onChange}
									className={"ft-eng-mod-taxest"}
								/>

								<div className="eng-modal__sequential">
									<Checkbox
										title="Sequential Tasks"
										name="sequentialTasks"
										checked={sequentialTasks}
										onChange={this.onChange}
										className={"ft-eng-mod-seqtasks"}
									/>
									<Tooltip
										arrow
										title="Tasks run one at a time, in order. Completing a task hands the next one to its assignees and emails them, and a task can be sent back to the previous step with a reason. Turn off to let all tasks be worked on in parallel."
									>
										<Icon.Info size={13} className="eng-modal__sequential__info ft-eng-mod-seqtasks-info" />
									</Tooltip>
								</div>
							</div>

							<div className="eng-modal__body__fields__container">
								<SearchableDropdown
									showAll
									title={<RequiredLabel text="Workflow Type" />}
									onSelect={this.onFlowSelect}
									selectedName={flowName}
									type={"flows"}
									className={"ft-eng-mod-flow"}
								/>

								<SearchableDropdown
									showAll
									title={<RequiredLabel text="Status" />}
									type="flowStates"
									flowCode={flowCode}
									selectedName={selectedFlowStateName}
									onSelect={this.onFlowStateSelect}
									className={"ft-eng-mod-flowstate"}
								/>

								<SearchableDropdown
									showAll
									title="Priority"
									options={PRIORITY_OPTIONS}
									selectedName={this.state.priority ? WORKFLOW.priorities[this.state.priority].value : ""}
									onSelect={this.onPrioritySelect}
									selectEmptyOnClear={false}
									className={"ft-eng-mod-priority"}
								/>

								<DateSelector
									// TODO: this classname/component will be refactored in the final theme fixes pr
									containerClass="eng-modal__date-selector"
									title={<RequiredLabel text="Fiscal Year End" />}
									selectedDate={new Date(fiscalYearEnd)}
									onDateSelect={this.onFiscalYearEndSelect}
									className={"ft-eng-mod-fiscyearend"}
								/>

								<DateSelector
									containerClass="eng-modal__date-selector"
									title={<RequiredLabel text="Date In" />}									
									selectedDate={dateIn !== "" ? new Date(dateIn) : new Date()}
									onDateSelect={this.onDateInSelect}
									className={"ft-eng-mod-datein"}
								/>

								<DateSelector
									title={<RequiredLabel text="Due Date" />}
									selectedDate={dueDate !== "" ? new Date(dueDate) : new Date()}
									onDateSelect={this.onDueDateSelect}
									className={"ft-eng-mod-duedate"}
								/>
							</div>
						</div>

						<div className="eng-modal__body__settings">
							<Tabs className="eng-modal__body__settings__tabs" onSelect={this.onTabSelect} selected={selectedTab}>
								<Tab id={TABS.tasks} text="Tasks" />
								<Tab id={TABS.users} text="Users" />
								<Tab id={TABS.repeats} text="Repeat Settings" />
								<Tab id={TABS.budgets} text="Budgets" />
							</Tabs>

							{selectedTab === TABS.tasks && (
								<div className="eng-modal__body__settings__tasks">
									<div className="eng-modal__body__settings__tasks__list">
										{tasks.map((task, index) => (
											<EngagementTaskCard
												key={index}
												task={task}
												index={index}
												onRemoveTask={this.onRemoveTask}
												onUpdateEngTask={this.onUpdateEngTask}
											/>
										))}
									</div>

									<AddItemButton itemName="Task" onClick={this.onNewEngTask} />
								</div>
							)}

							{selectedTab === TABS.users && (
								<UserAssigner containerClass="eng-modal__body__user-assigner" onChange={this.onAssignedUsersChanged} users={assignedUsers} />
							)}
							{selectedTab === TABS.repeats && (
								<>
									<RRuleEditor onChange={this.onRRuleChange} rrule={rrule} />
									{rrule.length > 0 && (
										<>
											<div className="eng-modal__body__settings__divider" />
											<div className="eng-modal__body__settings__repeats__section">
												<RelativeDate title="Recurring Due Date" onChange={this.onRelativeDueDateChange} data={relativeDueDate} />
											</div>
										</>
									)}
								</>
							)}
							{selectedTab === TABS.budgets && (
								<div className="eng-modal__body__settings__budgets">
									<EngagementBudgetSettings ref={(ref) => (this.budgetSettingsRef = ref)} />

									<div className="eng-modal__body__settings__divider" />

									<div className="eng-modal__body__settings__budgets__list">
										{this.state.budgets.map((b, idx) => (
											<EngagementBudgetCard key={idx} budget={b} index={idx} onRemoveBudget={this.onRemoveBudget} />
										))}
									</div>

									<AddItemButton itemName="Budget" onClick={this.onAddBudget} />
								</div>
							)}
						</div>
					</div>
				</div>
				<div className="modal-footer">
					<Button view="secondary" text="Cancel" onClick={this.onClose} />
					<Button text="Create" onClick={this.onCreate} disabled={this.state.loading} />
				</div>
			</Modal>
		);
	}
}

export default EngagementModal;
